import type { AxiosInstance } from 'axios';

/** Matches backend `verifyAttestationToken` expiry AppError message. */
const ATTESTATION_EXPIRED_MESSAGE = /attestation token has expired/i;

/**
 * Opaque 401 messages returned when an endpoint refuses the wizard JWT chain
 * (`ORCHESTRATOR_UNAUTHORIZED_MESSAGE`, `PRODUCT_SELECTION_UNAUTHORIZED_MESSAGE`,
 * `ATTESTATION_VERIFICATION_FAILED_MESSAGE`). The backend deliberately hides which
 * check failed, and expiry is the only cause a customer hits, so the wizard restarts.
 */
const ATTESTATION_UNAUTHORIZED_MESSAGES = [/^unauthorized$/i, /^attestation verification failed$/i];

/**
 * Soft-fail endpoints: callers already catch failures and degrade UX (e.g. Service Step
 * pickup bootstrap). A 401 here must not wipe the whole wizard session (OM-770).
 */
const SOFT_FAIL_ATTESTATION_PATH = /\/pickup\/validate(?:\?|$)/i;

export interface AttestationExpiryHandlers {
  /** Clears wizard JWTs and navigates back to Ship From / Ship To. */
  onAttestationExpired: () => void;
}

/**
 * Reads the messages of the shared `{ errors: [{ message }] }` envelope, or null when the
 * response is not one of our API errors (e.g. an API Gateway authorizer or network failure).
 */
function readApiErrorMessages(error: unknown): string[] | null {
  if (error === null || typeof error !== 'object') {
    return null;
  }

  const response = (error as { response?: unknown }).response;
  if (response === null || typeof response !== 'object') {
    return null;
  }

  const data = (response as { data?: unknown }).data;
  if (data === null || typeof data !== 'object') {
    return null;
  }

  const errors = (data as { errors?: unknown }).errors;
  if (!Array.isArray(errors)) {
    return null;
  }

  return errors.flatMap((item) => {
    if (item === null || typeof item !== 'object') {
      return [];
    }
    const message = (item as { message?: unknown }).message;
    return typeof message === 'string' ? [message] : [];
  });
}

/**
 * Returns the request URL from an Axios-style error, when present.
 */
function readRequestUrl(error: unknown): string {
  if (error === null || typeof error !== 'object') {
    return '';
  }

  const config = (
    error as { config?: { url?: unknown }; response?: { config?: { url?: unknown } } }
  ).config;
  const responseConfig = (error as { response?: { config?: { url?: unknown } } }).response?.config;
  const url = config?.url ?? responseConfig?.url;
  return typeof url === 'string' ? url : '';
}

/**
 * True when the failing call is a soft-fail endpoint that must not restart the wizard.
 * Soft-fail covers opaque pickup-validate attestation refusals only — an explicit
 * "Attestation token has expired" message still wipes the session.
 */
function isSoftFailAttestationRequest(error: unknown): boolean {
  if (!SOFT_FAIL_ATTESTATION_PATH.test(readRequestUrl(error))) {
    return false;
  }

  const messages = readApiErrorMessages(error);
  if (messages === null) {
    return false;
  }

  return !messages.some((message) => ATTESTATION_EXPIRED_MESSAGE.test(message));
}

/**
 * Returns true when an API error reports that the wizard attestation chain is no longer usable:
 * either an explicit expiry message (400) or an attestation verify refusal (401).
 */
export function isAttestationExpiredError(error: unknown): boolean {
  const messages = readApiErrorMessages(error);
  if (messages === null) {
    return false;
  }

  if (messages.some((message) => ATTESTATION_EXPIRED_MESSAGE.test(message))) {
    return true;
  }

  const status = (error as { response?: { status?: unknown } }).response?.status;
  if (status !== 401) {
    return false;
  }

  return messages.some((message) =>
    ATTESTATION_UNAUTHORIZED_MESSAGES.some((pattern) => pattern.test(message)),
  );
}

/**
 * Axios response middleware: when any API rejects with an expired attestation token,
 * invokes the handler once so the wizard can clear JWTs and return to the address step.
 * Pickup validate 401s are excluded — Service Step soft-fails that path instead of restarting.
 */
export function attachAttestationExpiryInterceptor(
  axiosInstance: AxiosInstance,
  handlers: AttestationExpiryHandlers,
): void {
  let redirectInFlight = false;

  axiosInstance.interceptors.response.use(
    (response) => response,
    (error: unknown) => {
      if (
        isAttestationExpiredError(error) &&
        !isSoftFailAttestationRequest(error) &&
        !redirectInFlight
      ) {
        redirectInFlight = true;
        try {
          handlers.onAttestationExpired();
        } finally {
          // Allow a later expiry (after user re-validates) to redirect again.
          queueMicrotask(() => {
            redirectInFlight = false;
          });
        }
      }

      if (error instanceof Error) {
        throw error;
      }

      throw new Error('API request failed', { cause: error });
    },
  );
}
